import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { catchError, concatMap, Observable, of, tap } from 'rxjs';
import { Store } from '@ngxs/store';
import { HttpClient } from '@angular/common/http';
import { IdTokenClaims } from '../../shared/interfaces/IdTokenClaims';
import { LoadPersistentFilters, SetUser } from '../../shared/states/user.actions';
import { IDifaUserBE } from '../../shared/interfaces/IDifaUserBE';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { SessionSyncService } from 'src/app/shared/services/session-sync.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private msalService = inject(MsalService);
  private msalBroadcastService = inject(MsalBroadcastService);
  private store = inject(Store);
  private httpClient = inject(HttpClient);
  private router = inject(Router);
  private sessionSyncService = inject(SessionSyncService);

  private _isAuthenticated: WritableSignal<boolean> = signal<boolean>(false);
  private _isAuthLoading: WritableSignal<boolean> = signal<boolean>(true);
  private _userAccount: WritableSignal<AccountInfo | null> = signal<AccountInfo | null>(null);
  private _impersonatedUser: WritableSignal<string | null> = signal<string | null>(null);

  public isAuthenticated: Signal<boolean> = computed(() => this._isAuthenticated());
  public isAuthLoading: Signal<boolean> = computed(() => this._isAuthLoading());
  public userIdTokenClaims: Signal<IdTokenClaims | null> = computed(() => this._userAccount()?.idTokenClaims as IdTokenClaims);
  public impersonatedUser: Signal<string | null> = computed(() => this._impersonatedUser());

  constructor() {
    this.initializeAuth();
  }

  public login(): void {
    this.msalService.loginRedirect();
  }

  public logout(): void {
    this.sessionSyncService.broadcastSignOut();
    this.msalService.logoutRedirect().subscribe();
  }

  private initializeAuth(): void {
    this.subscribeToLoginAndLogout();
    this.subscribeToAuthenticationDone();
  }

  private subscribeToAuthenticationDone() {
    this.msalBroadcastService.inProgress$
      .pipe(
        tap((status: InteractionStatus) => {
          this._isAuthLoading.set(false);

          if (status === InteractionStatus.None) {
            this.updateAuthState();
          }
        }),
      )
      .subscribe();
  }

  private subscribeToLoginAndLogout() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        tap((msg: EventMessage) => {
          if (msg.eventType === EventType.LOGOUT_SUCCESS) {
            this._isAuthenticated.set(false);
            this._userAccount.set(null);
          }
        }),
      )
      .subscribe();
  }

  public updateAuthState(): void {
    const accounts = this.msalService.instance.getAllAccounts();

    if (accounts.length > 0) {
      const activeAccount = this.msalService.instance.getActiveAccount() || accounts[0];

      if (activeAccount) {
        this._impersonatedUser.set(localStorage.getItem('impersonatedUser'));
        this.msalService.instance.setActiveAccount(activeAccount);
        this._userAccount.set(activeAccount);
        this.setUser().subscribe();
        this._isAuthenticated.set(true);
      }
    } else {
      this._isAuthenticated.set(false);
      this._userAccount.set(null);
    }
  }

  private setUser(): Observable<void> {
    return this.getUserProfile().pipe(
      tap(() => this._isAuthLoading.set(false)),
      catchError(error => {
        this._isAuthenticated.set(false);
        this._userAccount.set(null);
        // Only allowed GIDS is status 503 if a not allowed gid tries to login, status 0 is for network errors (database down etc.)
        if (error.status === 503 || error.status === 0) {
          void this.router.navigate(['/maintenance']);
        }
        return of(null);
      }),
      concatMap(user => this.store.dispatch(new SetUser(user))),
      concatMap(() => this.store.dispatch(new LoadPersistentFilters())),
    );
  }

  public setImpersonatingUser(gid: string) {
    this._impersonatedUser.set(gid);
    if (gid) {
      localStorage.setItem('impersonatedUser', gid);
    } else {
      localStorage.removeItem('impersonatedUser');
    }
  }

  private getUserProfile(): Observable<IDifaUserBE> {
    return this.httpClient.get<IDifaUserBE>(environment.profilePath);
  }
}
